/**
 * Store Management Screen Desktop
 * 
 * Desktop version of the store management interface for managers.
 * Provides comprehensive store management with filtering, sorting, and bulk actions.
 * 
 * @module StoreManagementScreenDesktop
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Search, Plus, RefreshCw, Download, UserCheck, Filter, X, Upload, FileSpreadsheet, ChevronDown, Star, Globe, Database, CheckCircle } from 'lucide-react';
import { CSVExportService } from '../../services/CSVExportService';
import toast from 'react-hot-toast';
import { SortableFilterableTableHeader, ColumnFilter, ColumnSort } from '../../components/common/SortableFilterableTableHeader';
import { useStores } from '../../hooks/useStores';
import { useAuth } from '../../contexts/AuthContext';
import { StoreTable } from '../../components/manager/StoreTable';
import { StoreForm } from '../../components/manager/StoreForm';
import { AdvancedStoreFilters, NotesFilterCriteria, getDefaultNotesFilters } from '../../components/manager/AdvancedStoreFilters';
import { StoreService } from '../../services/StoreService';
import { StoreAssignmentService } from '../../services/StoreAssignmentService';
import { Store, StoreFilters, StoreStatus } from '../../@types/store';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';
import { collection, getDocs, getDocsFromServer, query, where, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { UserProfile } from '../../@types/user';
import { getAssignedStores } from '../../utils/storeAssignment';
import type { Store as StoreType } from '../../@types/store';

/**
 * Styled component for spinning animation
 */
const SpinningIcon = styled(RefreshCw)`
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  animation: spin 1s linear infinite;
`;

/**
 * Styled Components
 */
const Header = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e5e7eb;
`;

const Tab = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background-color: ${props => props.active ? '#fff' : 'transparent'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#A0C93F' : 'transparent'};
  cursor: pointer;
  font-size: 15px;
  font-weight: ${props => props.active ? '600' : '500'};
  color: ${props => props.active ? '#A0C93F' : '#6b7280'};
  transition: all 0.2s ease;
  position: relative;
  margin-bottom: -2px;

  &:hover {
    color: #A0C93F;
    background-color: #f9fafb;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TabBadge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background-color: ${props => props.active ? '#A0C93F' : '#e5e7eb'};
  color: ${props => props.active ? '#fff' : '#6b7280'};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 4px;
`;

type TabType = 'assigned' | 'all';

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${Spacing.lg};
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  position: relative;
  min-width: 300px;

  svg {
    position: absolute;
    left: ${Spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md} ${Spacing.sm} 40px;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  background: ${props => {
    switch (props.variant) {
      case 'primary': return ThemeColors.primary.main;
      case 'success': return ThemeColors.success.main;
      case 'danger': return ThemeColors.error.main;
      default: return 'white';
    }
  }};
  color: ${props => props.variant === 'secondary' ? ThemeColors.text.primary : 'white'};
  border: ${props => props.variant === 'secondary' ? `1px solid ${ThemeColors.border.main}` : 'none'};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BulkActionsBar = styled.div<{ visible: boolean }>`
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.md};
  background: ${ThemeColors.primary.light};
  border-radius: 8px;
  margin-bottom: ${Spacing.lg};
`;

const SelectedCount = styled.span`
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.primary.main};
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
  padding: ${Spacing.md};
  background: ${ThemeColors.background.cards};
  border-radius: 8px;
  border: 1px solid ${ThemeColors.border.light};
  margin-bottom: ${Spacing.lg};
`;

const FilterLabel = styled.label`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
`;

const StoreLevelFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const StoreLevelSliderWrapper = styled.div`
  position: relative;
  padding: ${Spacing.md} 0;
`;

const StoreLevelSlider = styled.input.attrs({ type: 'range' })`
  width: 100%;
  height: 8px;
  border-radius: 8px;
  background: ${ThemeColors.gray[200]};
  outline: none;
  -webkit-appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${ThemeColors.primary.main};
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    transition: all 0.2s ease;
    
    &:hover {
      transform: scale(1.1);
    }
  }
  
  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${ThemeColors.primary.main};
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    transition: all 0.2s ease;
    
    &:hover {
      transform: scale(1.1);
    }
  }
`;

const StoreLevelDots = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${Spacing.xs};
  padding: 0 ${Spacing.xs};
`;

const StoreLevelDot = styled.div<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.active ? ThemeColors.primary.main : ThemeColors.gray[300]};
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.2);
  }
`;

const StoreLevelLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${Spacing.xs};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.hint};
  padding: 0 ${Spacing.xs};
`;

const StoreLevelValue = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${Spacing.xs};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const StoreLevelValueDisplay = styled.span`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.primary.main};
`;

const ExportDropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ExportDropdown = styled.div<{ show: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 180px;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 0.2s ease;
`;

const ExportDropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  background: transparent;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${ThemeColors.background.secondary};
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  svg {
    width: 18px;
    height: 18px;
    color: ${ThemeColors.text.secondary};
  }
`;

const ExportDropdownDivider = styled.div`
  height: 1px;
  background: ${ThemeColors.border.light};
  margin: ${Spacing.xs} 0;
`;

const ExportDropdownHeader = styled.div`
  padding: ${Spacing.sm} ${Spacing.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.hint};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ExportDropdownSectionLabel = styled.div`
  padding: ${Spacing.xs} ${Spacing.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: ${ThemeColors.primary.light};
`;

const ProgressOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProgressCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px 40px;
  min-width: 340px;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
`;

const ProgressTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const ProgressSubtitle = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: ${ThemeColors.gray[200]};
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${props => props.pct}%;
  background: ${ThemeColors.primary.main};
  border-radius: 999px;
  transition: width 0.3s ease;
`;

const ProgressCount = styled.span`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.primary.main};
`;

/**
 * Store Management Screen Desktop Component
 */
export const StoreManagementScreenDesktop: React.FC = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const { t } = useTranslation();
  const isHandLead = userRole === 'hand-lead';
  
  const {
    stores,
    isLoading,
    error,
    hasMore,
    fetchStores,
    loadMore,
    refreshStores,
    clearError
  } = useStores();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [salesReps, setSalesReps] = useState<Array<{ id: string; name: string; username?: string; salesGroup?: string }>>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [columnSort, setColumnSort] = useState<ColumnSort | null>(null);
  const [storeLevelMin, setStoreLevelMin] = useState<number>(0);
  const [storeLevelMax, setStoreLevelMax] = useState<number>(100);
  const [showStoreLevelFilter, setShowStoreLevelFilter] = useState<boolean>(false);
  
  // Advanced notes filters state
  const [notesFilters, setNotesFilters] = useState<NotesFilterCriteria>(getDefaultNotesFilters());
  const [filteredByNotes, setFilteredByNotes] = useState<Store[]>([]);
  const [isFilteringByNotes, setIsFilteringByNotes] = useState(false);
  
  // For search: Load all stores once (like sales rep page)
  const [allStoresForSearch, setAllStoresForSearch] = useState<Store[]>([]);
  const [isLoadingAllStores, setIsLoadingAllStores] = useState(false);
  // Increment to force refetch of search list (e.g. after delete or refresh)
  const [searchRefreshKey, setSearchRefreshKey] = useState(0);
  // IDs of stores deleted this session — exclude from display until refetch completes
  const [deletedStoreIds, setDeletedStoreIds] = useState<Set<string>>(new Set());
  
  // For hand-lead: Tab state and assigned stores
  const [activeTab, setActiveTab] = useState<TabType>('assigned');
  const [assignedStores, setAssignedStores] = useState<Store[]>([]);
  const [isLoadingAssignedStores, setIsLoadingAssignedStores] = useState(false);
  
  // Total stores count from database (for "All Stores" badge)
  const [totalStoresCount, setTotalStoresCount] = useState<number>(0);
  
  // Export state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showBulkExportDropdown, setShowBulkExportDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const bulkExportDropdownRef = useRef<HTMLDivElement>(null);

  // Export progress overlay state
  const [exportProgress, setExportProgress] = useState<{
    visible: boolean;
    phase: 'fetching' | 'building' | 'done';
    loaded: number;
    total: number;
    format: 'CSV' | 'Excel';
  }>({ visible: false, phase: 'fetching', loaded: 0, total: 0, format: 'CSV' });
  
  // Debounce search input to avoid excessive queries
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Debounce search - wait 400ms after user stops typing
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  /**
   * Load total stores count from database
   */
  useEffect(() => {
    const loadTotalStoresCount = async () => {
      try {
        const storesRef = collection(db, 'Stores');
        const querySnapshot = await getDocs(storesRef);
        const totalCount = querySnapshot.size;
        setTotalStoresCount(totalCount);
        console.log(`[StoreManagement] Total stores in database: ${totalCount}`);
      } catch (error) {
        console.error('[StoreManagement] Error loading total stores count:', error);
      }
    };
    
    loadTotalStoresCount();
  }, []);

  /**
   * Load sales reps on component mount
   */
  useEffect(() => {
    const loadSalesReps = async () => {
      try {
        const usersRef = collection(db, 'users');
        // Include both sales-representative and hand-lead for the Sales Representative filter
        const q = query(usersRef, where('role', 'in', ['sales-representative', 'hand-lead']));
        const usersSnapshot = await getDocs(q);
        const repsList = usersSnapshot.docs.map(doc => {
          const data = doc.data() as UserProfile;
          return {
            id: doc.id,
            name: data.displayName || data.firstName || data.email || 'Unknown',
            username: data.username || data.email?.split('@')[0] || '',
            salesGroup: data.salesGroup || ''
          };
        });
        setSalesReps(repsList);
      } catch (error) {
        console.error('Error loading sales reps:', error);
      }
    };
    
    loadSalesReps();
  }, []);

  /**
   * Load assigned stores for hand-lead users
   */
  useEffect(() => {
    const loadAssignedStores = async () => {
      if (!isHandLead || !user?.id) {
        return;
      }

      setIsLoadingAssignedStores(true);
      try {
        const storesRef = collection(db, 'Stores');
        const querySnapshot = await getDocs(storesRef);
        
        const storeTypesData: StoreType[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          storeTypesData.push({
            id: doc.id,
            CustomerNo: data.CustomerNo || '',
            StoreName: data.StoreName || 'Unknown Store',
            Address: data.Address || '',
            City: data.City || '',
            Prov: data.Prov || '',
            PostalCode: data.PostalCode || '',
            Telephone: data.Telephone || '',
            EmailAdddress: data.EmailAdddress || '',
            SalespersonNo: data.SalespersonNo || '',
            salesGroup: data.SalesGroup || '',
            assignedToUserIds: data.assignedToUserIds || [],
            status: 'active',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          });
        });

        const userDoc = await getDoc(doc(db, 'users', user.id));
        const userData = userDoc.data();

        const assigned = await getAssignedStores(
          user.id, 
          storeTypesData, 
          userData?.salesGroup,
          userData?.assignedStoreIds
        );
        
        const assignedData: Store[] = assigned.map(s => StoreService.mapDbDataToStore(s.id, {
          CustomerNo: s.CustomerNo,
          StoreName: s.StoreName,
          Address: s.Address,
          City: s.City,
          Prov: s.Prov,
          PostalCode: s.PostalCode,
          Telephone: s.Telephone,
          EmailAdddress: s.EmailAdddress,
          SalespersonNo: s.SalespersonNo,
          SalesGroup: s.salesGroup,
          assignedToUserIds: s.assignedToUserIds,
        }));

        console.log(`Assigned stores for hand-lead user ${user.id}:`, assignedData.length);
        setAssignedStores(assignedData);
      } catch (error) {
        console.error('Error loading assigned stores:', error);
      } finally {
        setIsLoadingAssignedStores(false);
      }
    };

    loadAssignedStores();
  }, [isHandLead, user]);

  /**
   * Load ALL stores when searching (like sales rep page)
   * This ensures search works across entire database, not just loaded stores
   * Also applies column filters if they're active (so search + filters work together)
   */
  useEffect(() => {
    const loadAllStoresForSearch = async () => {
      if (!debouncedSearchTerm?.trim()) {
        // Clear all stores when search is cleared
        setAllStoresForSearch([]);
        return;
      }

      setIsLoadingAllStores(true);
      setAllStoresForSearch([]); // Clear so we don't show stale results while fetching from DB
      try {
        console.log('[StoreManagement] Loading ALL stores for search...', {
          searchTerm: debouncedSearchTerm,
          columnFiltersCount: columnFilters.length
        });
        
        // Load ALL stores directly from Firestore without any limit
        // Use getDocsFromServer so we get fresh data from server, not cache
        // (otherwise a hard-deleted store can still appear from local cache)
        const storesRef = collection(db, 'Stores');
        const querySnapshot = await getDocsFromServer(storesRef);
        
        console.log(`[StoreManagement] Loaded ${querySnapshot.docs.length} stores from database for search`);
        
        // Exclude archived at doc level (handles any field casing) so deleted stores never show
        const docsToInclude = querySnapshot.docs.filter(d => {
          const data = d.data();
          const status = data.status ?? data.Status;
          return status !== 'archived';
        });
        
        const allStores: Store[] = docsToInclude
          .map(doc => StoreService.mapDbDataToStore(doc.id, doc.data()))
          .filter(s => s.status !== 'archived');
        
        // Log sample of store names to help debug search issues
        const sampleStoreNames = allStores.slice(0, 10).map(s => ({
          id: s.id,
          name: s.name,
          storeName: (s as any).StoreName,
          rawStoreName: (s as any)._rawData?.StoreName
        }));
        console.log('[StoreManagement] Sample store names:', sampleStoreNames);
        
        setAllStoresForSearch(allStores);
      } catch (error) {
        console.error('[StoreManagement] Error loading all stores for search:', error);
        // Clear so we don't show stale/cached data if server read failed
        setAllStoresForSearch([]);
      } finally {
        setIsLoadingAllStores(false);
      }
    };

    loadAllStoresForSearch();
  }, [debouncedSearchTerm, searchRefreshKey]);

  /**
   * Load stores on component mount and when filters change
   * Always queries database when filters/search/sort change (resets pagination)
   * Uses debounced search term to avoid excessive queries
   * 
   * IMPORTANT: Column filters ALWAYS query the entire database, not just displayed stores
   * When searching, we load all stores and filter client-side (including column filters)
   * When not searching, column filters are applied at database level via StoreService
   */
  useEffect(() => {
    // If searching, skip this effect - we'll handle search separately (loads all stores)
    // Column filters will be applied client-side to search results
    if (debouncedSearchTerm?.trim()) {
      return;
    }

    // For hand-lead on assigned tab, skip fetching (we use assignedStores)
    if (isHandLead && activeTab === 'assigned') {
      return;
    }

    const filters: StoreFilters = {};

    // Apply column filters - map column keys to StoreFilters
    // Support both single values and arrays (multi-select)
    // Best Practice: Pass arrays directly to StoreService which uses Firestore 'in' operator
    // These filters query the ENTIRE database, not just displayed stores
    columnFilters.forEach(filter => {
      if (filter.value) {
        const values = Array.isArray(filter.value) ? filter.value : [filter.value];
        const filterValue = values.length === 1 ? values[0] : values; // Pass array for multi-select
        
        switch (filter.column) {
          case 'customerNo':
            // Customer number filter - use direct database filter with 'in' operator
            filters.customerNo = filterValue as string | string[];
            break;
          case 'name':
            // Store name filter - use direct database filter with 'in' operator
            filters.storeName = filterValue as string | string[];
            break;
          case 'salespersonNo':
            // Pass array for multi-select - StoreService will use 'in' operator
            filters.salesRepId = filterValue as string | string[];
            break;
          case 'city':
            // Pass array for multi-select - StoreService will use 'in' operator
            filters.city = filterValue as string | string[];
            break;
          case 'salesGroup':
            // Pass array for multi-select - StoreService will use 'in' operator
            filters.salesGroup = filterValue as string | string[];
            break;
          case 'sortField':
            // Pass array for multi-select - StoreService will use 'in' operator
            filters.sortField = filterValue as string | string[];
            break;
          case 'province':
          case 'state':
            // Pass array for multi-select - StoreService will use 'in' operator
            filters.state = filterValue as string | string[];
            break;
        }
      }
    });

    // Store level filter disabled for now
    // if (storeLevelMin > 0 || storeLevelMax < 100) {
    //   filters.storeLevelMin = storeLevelMin;
    //   filters.storeLevelMax = storeLevelMax;
    // }
    
    // Apply sorting from columnSort if available
    // Map all sortable fields to their database field names
    // For computed fields (notesCount, lastVisit), we'll sort client-side after fetching notes
    let sortField: string = 'name'; // Default sort
    const isComputedFieldSort = columnSort?.column === 'notesCount' || columnSort?.column === 'lastVisit';
    
    if (columnSort?.column && !isComputedFieldSort) {
      switch (columnSort.column) {
        case 'customerNo':
          sortField = 'customerNo';
          break;
        case 'name':
          sortField = 'name';
          break;
        case 'city':
          sortField = 'city';
          break;
        case 'salespersonNo':
          sortField = 'salespersonNo';
          break;
        case 'salesGroup':
          sortField = 'salesGroup';
          break;
        case 'sortField':
          sortField = 'sortField';
          break;
        default:
          sortField = 'name';
      }
    }
    const sortDirection = columnSort?.direction === 'desc' ? 'desc' : 'asc';
    
    // For computed field sorting (notesCount, lastVisit), we need to fetch more stores
    // to sort them correctly across all stores, not just the first 50
    // When column filters are active, fetch ALL matching stores (no pagination)
    const hasColumnFilters = columnFilters.length > 0 && columnFilters.some(f => f.value);
    
    // When filters are active, use a very large pageSize to fetch ALL matching stores
    // This ensures all filtered results are displayed without needing "Load More"
    // For normal browsing without filters, use standard pagination (50 stores)
    // Note: StoreService will handle fetching up to 9999 stores when filters are active
    const pageSizeForSort = isComputedFieldSort || hasColumnFilters ? 9999 : 50;
    
    // Always fetch from database when filters/search/sort change
    // This ensures we query ALL stores matching the criteria, not just cached/visible ones
    // StoreService.getStores applies filters at database level, querying the entire database
    console.log('[StoreManagement] Fetching stores from database with filters:', filters, 'sort:', { field: sortField, direction: sortDirection }, 'pageSize:', pageSizeForSort, 'hasColumnFilters:', hasColumnFilters);
    fetchStores(filters, { field: sortField, direction: sortDirection }, pageSizeForSort);
  }, [debouncedSearchTerm, columnFilters, columnSort, fetchStores, isHandLead, activeTab]);

  /**
   * Clear success message after 5 seconds
   */
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  /**
   * Apply store visit-based filtering when stores or visit filters change
   */
  useEffect(() => {
    const applyNotesFilter = async () => {
      // Check if any notes filter is active
      const hasActiveNotesFilter = 
        notesFilters.competitors.length > 0 ||
        notesFilters.storeLevelRange !== null ||
        notesFilters.lowStockCategories.length > 0 ||
        notesFilters.needsManagerContact !== null ||
        notesFilters.hasNotes !== null ||
        (notesFilters.visitTypes && notesFilters.visitTypes.length > 0) ||
        (notesFilters.visitDateRange !== null && (notesFilters.visitDateRange.from || notesFilters.visitDateRange.to)) ||
        (notesFilters.salesRepIds && notesFilters.salesRepIds.length > 0);

      if (!hasActiveNotesFilter) {
        setFilteredByNotes([]);
        return;
      }

      setIsFilteringByNotes(true);
      
      // Add timeout to prevent infinite loading (30 seconds)
      const timeoutId = setTimeout(() => {
        console.error('[StoreManagement] Filter operation timed out after 30 seconds');
        setIsFilteringByNotes(false);
        toast.error('Filter operation timed out. The database may be large. Please try with more specific filters.');
        setFilteredByNotes([]);
      }, 30000);
      
      try {
        // IMPORTANT: For note-based filtering, we need to check ALL stores in the database,
        // not just the currently loaded stores. Otherwise, filters will only work on visible stores.
        // Fetch all store IDs from the database for comprehensive filtering
        let storeIds: string[] = [];
        
        try {
          console.log('[StoreManagement] Fetching all store IDs for filtering...');
          const storesRef = collection(db, 'Stores');
          const allStoresSnapshot = await getDocs(storesRef);
          storeIds = allStoresSnapshot.docs.map(doc => doc.id);
          console.log(`[StoreManagement] ✓ Fetched ${storeIds.length} store IDs from database for filtering`);
        } catch (error: any) {
          clearTimeout(timeoutId);
          console.error('[StoreManagement] Error fetching all stores for filtering:', error);
          setIsFilteringByNotes(false);
          toast.error(`Failed to fetch stores: ${error.message || 'Unknown error'}`);
          setFilteredByNotes([]);
          return;
        }
        
        if (storeIds.length === 0) {
          clearTimeout(timeoutId);
          setFilteredByNotes([]);
          setIsFilteringByNotes(false);
          console.warn('[StoreManagement] No stores found in database');
          return;
        }
        
        console.log('[StoreManagement] Starting filter operation:', {
          storeIdsCount: storeIds.length,
          filters: notesFilters
        });
        
        // Create sales rep map for matching note authors to sales rep IDs
        const salesRepMap = new Map<string, { email?: string; name?: string }>();
        salesReps.forEach(rep => {
          salesRepMap.set(rep.id, {
            email: rep.username || rep.name, // Use username or name as email
            name: rep.name
          });
        });
        
        const matchingIds = await StoreService.filterStoresByNotes(storeIds, {
          competitors: notesFilters.competitors,
          storeLevelRange: notesFilters.storeLevelRange,
          lowStockCategories: notesFilters.lowStockCategories,
          needsManagerContact: notesFilters.needsManagerContact,
          hasNotes: notesFilters.hasNotes,
          visitTypes: notesFilters.visitTypes,
          visitDateRange: notesFilters.visitDateRange,
          salesRepIds: notesFilters.salesRepIds,
        }, salesRepMap);
        
        console.log('[StoreManagement] Filter results:', {
          totalStores: storeIds.length,
          matchingStores: matchingIds.length,
          matchingIds: matchingIds.slice(0, 10), // Log first 10 for debugging
          filters: notesFilters
        });
        
        // Fetch full store data for matching stores from database
        // We can't just filter the loaded stores because they might not include all matching stores
        const matchingStores: Store[] = [];
        
        if (matchingIds.length > 0) {
          console.log(`[StoreManagement] Fetching store data for ${matchingIds.length} matching stores...`);
          
          // Fetch store data in batches to avoid overwhelming Firestore
          const batchSize = 50;
          let fetchedCount = 0;
          let errorCount = 0;
          
          for (let i = 0; i < matchingIds.length; i += batchSize) {
            const batch = matchingIds.slice(i, i + batchSize);
            const batchPromises = batch.map(async (storeId) => {
              try {
                const storeDoc = await getDoc(doc(db, 'Stores', storeId));
                if (storeDoc.exists()) {
                  // Use StoreService's mapping function to convert Firestore data to Store interface
                  const storeData = storeDoc.data();
                  const store = StoreService.mapDbDataToStore(storeId, storeData);
                  fetchedCount++;
                  return store;
                } else {
                  console.warn(`[StoreManagement] Store ${storeId} does not exist in database`);
                  errorCount++;
                return null;
                }
              } catch (error) {
                console.error(`[StoreManagement] Error fetching store ${storeId}:`, error);
                errorCount++;
                return null;
              }
            });
            
            const batchResults = await Promise.all(batchPromises);
            matchingStores.push(...batchResults.filter((s): s is Store => s !== null));
          }
          
          console.log(`[StoreManagement] ✓ Loaded ${matchingStores.length} matching stores (${fetchedCount} fetched, ${errorCount} errors)`);
          
          if (matchingStores.length === 0) {
            console.error('[StoreManagement] ⚠️ No stores were successfully fetched despite having matching IDs!');
            console.error('[StoreManagement] This might indicate:');
            console.error('   1. Store documents were deleted');
            console.error('   2. Permission issues');
            console.error('   3. Store IDs from filter don\'t match actual store documents');
          }
          
          // IMPORTANT: Fetch notes data for matching stores to populate lastVisit and notesCount
          console.log('[StoreManagement] Fetching notes data for matching stores...');
          const matchingStoreIds = matchingStores.map(s => s.id);
          const notesDataMap = await StoreService.batchGetNotesData(matchingStoreIds);
          
          // Apply notes data to stores
          let storesWithNotes = 0;
          matchingStores.forEach(store => {
            const notesData = notesDataMap.get(store.id) || { count: 0, lastDate: null };
            store.notesCount = notesData.count;
            store.lastNoteDate = notesData.lastDate || undefined;
            if (notesData.lastDate) {
              store.lastVisit = notesData.lastDate;
            }
            if (notesData.count > 0) {
              storesWithNotes++;
            }
          });
          
          console.log(`[StoreManagement] ✓ Applied notes data to ${matchingStores.length} stores (${storesWithNotes} have visits)`);
          
          // Debug: Log sample stores
          if (matchingStores.length > 0) {
            const sampleStores = matchingStores.slice(0, 3);
            console.log('[StoreManagement] Sample matching stores:', sampleStores.map(s => ({
              id: s.id,
              name: s.name,
              notesCount: s.notesCount,
              lastVisit: s.lastNoteDate ? (s.lastNoteDate instanceof Date ? s.lastNoteDate.toISOString().split('T')[0] : String(s.lastNoteDate)) : 'Never'
            })));
          }
        } else {
          console.warn('[StoreManagement] ⚠️ No stores matched the filter criteria');
          console.warn('[StoreManagement] Filter criteria:', JSON.stringify(notesFilters, null, 2));
          console.warn('[StoreManagement] This might indicate:');
          console.warn('   1. No notes match the filter criteria');
          console.warn('   2. Query failed and fallback returned no results');
          console.warn('   3. Date range is too narrow');
        }
        
        clearTimeout(timeoutId);
        setFilteredByNotes(matchingStores);
        
        if (matchingStores.length === 0) {
          const filterSummary = Object.entries(notesFilters)
            .filter(([_, value]) => {
              if (Array.isArray(value)) return value.length > 0;
              if (typeof value === 'object' && value !== null) return true;
              return value !== null && value !== undefined;
            })
            .map(([key]) => key)
            .join(', ');
          
          toast(`No stores match the selected filter criteria${filterSummary ? ` (${filterSummary})` : ''}`, {
            duration: 5000
          });
        } else {
          toast.success(`Found ${matchingStores.length} store${matchingStores.length === 1 ? '' : 's'} matching the filters`, {
            duration: 3000
          });
        }
      } catch (error: any) {
        clearTimeout(timeoutId);
        console.error('[StoreManagement] Error filtering stores by notes:', error);
        toast.error(`Filter error: ${error.message || 'Unknown error'}`);
        setFilteredByNotes([]);
      } finally {
        setIsFilteringByNotes(false);
      }
    };

    applyNotesFilter();
  }, [stores, notesFilters]);

  /**
   * Handle notes filter change
   */
  const handleNotesFilterChange = useCallback((filters: NotesFilterCriteria) => {
    setNotesFilters(filters);
  }, []);

  /**
   * Get stores to display - either all stores or filtered by notes
   */
  // Ensure notes data is always loaded for displayed stores
  useEffect(() => {
    const loadNotesForDisplayedStores = async () => {
      // Only load if no active notes filter (when using regular stores)
      const hasActiveNotesFilter = 
        notesFilters.competitors.length > 0 ||
        notesFilters.storeLevelRange !== null ||
        notesFilters.lowStockCategories.length > 0 ||
        notesFilters.needsManagerContact !== null ||
        notesFilters.hasNotes !== null ||
        (notesFilters.visitTypes && notesFilters.visitTypes.length > 0) ||
        (notesFilters.visitDateRange !== null && (notesFilters.visitDateRange.from || notesFilters.visitDateRange.to)) ||
        (notesFilters.salesRepIds && notesFilters.salesRepIds.length > 0);

      if (!hasActiveNotesFilter && stores.length > 0) {
        // Check if stores already have notes data
        const storesNeedingNotes = stores.filter(s => 
          s.notesCount === undefined && s.lastNoteDate === undefined
        );
        
        if (storesNeedingNotes.length > 0) {
          console.log(`[StoreManagement] Loading notes data for ${storesNeedingNotes.length} stores...`);
          try {
            const storeIds = storesNeedingNotes.map(s => s.id);
            const notesDataMap = await StoreService.batchGetNotesData(storeIds);
            
            // Update stores with notes data
            storesNeedingNotes.forEach(store => {
              const notesData = notesDataMap.get(store.id) || { count: 0, lastDate: null };
              store.notesCount = notesData.count;
              store.lastNoteDate = notesData.lastDate || undefined;
              if (notesData.lastDate) {
                store.lastVisit = notesData.lastDate;
              }
            });
            
            console.log(`[StoreManagement] ✓ Loaded notes data for ${storesNeedingNotes.length} stores`);
          } catch (error: any) {
            console.error('[StoreManagement] Error loading notes data:', error);
            if (error?.message?.includes('index')) {
              const indexUrl = error.message.match(/https:\/\/[^\s]+/)?.[0] || 
                              'https://console.firebase.google.com/project/erp-mock/firestore/indexes';
              console.error(`%c[StoreManagement] 🔗 Missing Index! Create it here: ${indexUrl}`, 'color: red; font-weight: bold;');
            }
          }
        }
      }
    };
    
    loadNotesForDisplayedStores();
  }, [stores, notesFilters]);

  // Filter stores by search term AND column filters (client-side, like sales rep page)
  // This ensures both search and column filters work together and query the entire database
  const filteredBySearch = useMemo(() => {
    if (!debouncedSearchTerm?.trim() || allStoresForSearch.length === 0) {
      return [];
    }

    const query = debouncedSearchTerm.toLowerCase().trim();
    console.log(`[StoreManagement] Filtering ${allStoresForSearch.length} stores for search: "${query}" with ${columnFilters.length} column filters`);
    
    // First apply search term filter
    // Check both mapped fields and raw database fields to catch all variations
    let filtered = allStoresForSearch.filter(store => {
      const rawData = (store as any)._rawData || {};
      
      // Helper to safely check if a value contains the query
      const matches = (value: any): boolean => {
        if (!value) return false;
        return String(value).toLowerCase().includes(query);
      };
      
      return (
        matches(store.customerNo) ||
        matches((store as any).CustomerNo) ||
        matches(store.name) ||
        matches((store as any).StoreName) ||
        matches((store as any).Name) ||
        matches(rawData.StoreName) ||
        matches(rawData.Name) ||
        matches(rawData.name) ||
        matches(store.city) ||
        matches((store as any).City) ||
        matches(rawData.City) ||
        matches(rawData.city) ||
        matches(store.address) ||
        matches((store as any).Address) ||
        matches(rawData.Address) ||
        matches(rawData.address) ||
        matches(store.email) ||
        matches((store as any).Email) ||
        matches(rawData.EmailAdddress) ||
        matches(rawData.EmailAddress) ||
        matches(rawData.Email) ||
        matches(rawData.email) ||
        matches(store.phone) ||
        matches(store.telephone) ||
        matches((store as any).Telephone) ||
        matches((store as any).Phone) ||
        matches(rawData.Telephone) ||
        matches(rawData.Phone) ||
        matches(rawData.phone) ||
        matches(store.salesGroup) ||
        matches((store as any).SalesGroup) ||
        matches(rawData.SalesGroup) ||
        matches(rawData.salesGroup) ||
        matches(store.salespersonNo) ||
        matches((store as any).SalespersonNo) ||
        matches(rawData.SalespersonNo) ||
        matches(rawData.salespersonNo) ||
        matches(store.salesPersonName) ||
        matches((store as any).SalesPersonName) ||
        matches(rawData.SalesPersonName) ||
        matches(rawData.salesPersonName) ||
        matches(store.sortField) ||
        matches((store as any).SortField) ||
        matches(rawData.SortField) ||
        matches(rawData.sortField) ||
        matches(store.state) ||
        matches(store.province) ||
        matches((store as any).Prov) ||
        matches((store as any).Province) ||
        matches(rawData.Prov) ||
        matches(rawData.Province) ||
        matches(rawData.province) ||
        matches(rawData.state) ||
        matches(store.pc) ||
        matches((store as any).PostalCode) ||
        matches((store as any).PC) ||
        matches(rawData.PostalCode) ||
        matches(rawData.PC) ||
        matches(rawData.pc) ||
        matches(store.storeNumber) ||
        matches((store as any).StoreNumber) ||
        matches(rawData.StoreNumber) ||
        matches(rawData.storeNumber)
      );
    });
    
    // Then apply column filters to search results
    columnFilters.forEach(filter => {
      if (filter.value && !filter.dateRange) {
        const filterValues = Array.isArray(filter.value) ? filter.value : [filter.value];
        filtered = filtered.filter(store => {
          let storeValue: string = '';
          
          switch (filter.column) {
            case 'customerNo':
              storeValue = store.customerNo || '';
              break;
            case 'name':
              storeValue = store.name || '';
              break;
            case 'salespersonNo':
              storeValue = store.salespersonNo || store.salesPersonName || 'Unassigned';
              break;
            case 'city':
              storeValue = store.city || '';
              break;
            case 'salesGroup':
              storeValue = store.salesGroup || (store as any).SalesGroup || '';
              break;
            case 'sortField':
              storeValue = store.sortField || (store as any).SortField || '';
              break;
            case 'province':
            case 'state':
              storeValue = store.state || store.province || (store as any).Prov || '';
              break;
            default:
              storeValue = String((store as any)[filter.column] || '');
          }
          
          const storeValueLower = storeValue.toLowerCase();
          return filterValues.some(filterVal => {
            const filterValStr = String(filterVal || '').toLowerCase();
            return storeValueLower === filterValStr;
          });
        });
      }
    });
    
    console.log(`[StoreManagement] Found ${filtered.length} stores matching search: "${query}" with column filters applied`);
    if (filtered.length > 0) {
      console.log('[StoreManagement] Sample matching stores:', filtered.slice(0, 10).map(s => ({
        id: s.id,
        name: s.name,
        storeName: (s as any).StoreName,
        rawStoreName: (s as any)._rawData?.StoreName,
        city: s.city,
        customerNo: s.customerNo
      })));
    } else {
      // If no results, log some sample stores to help debug
      console.log('[StoreManagement] No matches found. Sample of all stores:', allStoresForSearch.slice(0, 5).map(s => ({
        id: s.id,
        name: s.name,
        storeName: (s as any).StoreName,
        rawStoreName: (s as any)._rawData?.StoreName
      })));
    }
    
    return filtered;
  }, [allStoresForSearch, debouncedSearchTerm, columnFilters]);

  // Stable key for search-result store IDs (avoids effect re-run after we update state)
  const searchResultStoreKey = useMemo(() => {
    if (!debouncedSearchTerm?.trim() || filteredBySearch.length === 0) return '';
    return filteredBySearch.map(s => s.id).sort().join(',');
  }, [filteredBySearch, debouncedSearchTerm]);

  // Load notes data for search results — always fetch from Firebase (real-time)
  // Search loads stores via mapDbDataToStore which uses denormalized notesCount; that can be stale (0).
  useEffect(() => {
    if (!searchResultStoreKey) return;

    const loadNotesForSearchResults = async () => {
      const storeIds = filteredBySearch.map(s => s.id);
      console.log(`[StoreManagement] Loading notes data for ${storeIds.length} search results (real-time)...`);
      try {
        const notesDataMap = await StoreService.batchGetNotesData(storeIds);

        filteredBySearch.forEach(store => {
          const notesData = notesDataMap.get(store.id) || { count: 0, lastDate: null };
          store.notesCount = notesData.count;
          store.lastNoteDate = notesData.lastDate ?? undefined;
          if (notesData.lastDate) store.lastVisit = notesData.lastDate;
        });

        setAllStoresForSearch(prev => [...prev]);

        const withVisits = filteredBySearch.filter(s => (notesDataMap.get(s.id)?.count ?? 0) > 0).length;
        console.log(`[StoreManagement] ✓ Applied notes to ${storeIds.length} search results; ${withVisits} with visits`);
      } catch (error: any) {
        console.error('[StoreManagement] Error loading notes data for search results:', error);
      }
    };

    loadNotesForSearchResults();
  }, [searchResultStoreKey]);

  const displayedStores = useMemo(() => {
    const excludeDeleted = (list: Store[]) =>
      list.filter(s => !deletedStoreIds.has(s.id));

    // If user has a search term, always use search results (from DB) — never fall back to full list
    if (debouncedSearchTerm?.trim()) {
      console.log('[StoreManagement] Using search results:', { count: filteredBySearch.length, query: debouncedSearchTerm });
      return excludeDeleted(filteredBySearch);
    }

    const hasActiveNotesFilter = 
      notesFilters.competitors.length > 0 ||
      notesFilters.storeLevelRange !== null ||
      notesFilters.lowStockCategories.length > 0 ||
      notesFilters.needsManagerContact !== null ||
      notesFilters.hasNotes !== null ||
      (notesFilters.visitTypes && notesFilters.visitTypes.length > 0) ||
      (notesFilters.visitDateRange !== null && (notesFilters.visitDateRange.from || notesFilters.visitDateRange.to)) ||
      (notesFilters.salesRepIds && notesFilters.salesRepIds.length > 0);

    // Use base stores (filtered by tab for hand-lead)
    // For hand-lead users on "assigned" tab, use assignedStores; otherwise use stores
    const baseStores = (isHandLead && activeTab === 'assigned') ? assignedStores : stores;
    let resultStores = baseStores;

    if (hasActiveNotesFilter) {
      console.log('[StoreManagement] Using filteredByNotes:', {
        filteredByNotesCount: filteredByNotes.length,
        hasActiveNotesFilter,
        filters: notesFilters
      });
      // For hand-lead on assigned tab, filter notes results to only assigned stores
      if (isHandLead && activeTab === 'assigned') {
        const assignedStoreIds = new Set(assignedStores.map(s => s.id));
        return excludeDeleted(filteredByNotes.filter(s => assignedStoreIds.has(s.id)));
      }
      return excludeDeleted(filteredByNotes);
    }
    
    // Log stores with notes for debugging
    const storesWithNotes = resultStores.filter(s => (s.notesCount || 0) > 0);
    console.log('[StoreManagement] Using regular stores:', { 
      storesCount: resultStores.length,
      storesWithNotes: storesWithNotes.length,
      sampleStoresWithNotes: storesWithNotes.slice(0, 3).map(s => ({
        id: s.id,
        name: s.name,
        notesCount: s.notesCount,
        lastVisit: s.lastNoteDate ? (s.lastNoteDate instanceof Date ? s.lastNoteDate.toISOString().split('T')[0] : String(s.lastNoteDate)) : 'Never'
      }))
    });
    return excludeDeleted(resultStores);
  }, [stores, assignedStores, isHandLead, activeTab, filteredByNotes, notesFilters, filteredBySearch, debouncedSearchTerm, deletedStoreIds]);

  /**
   * Handle filter change from column filters
   */
  const handleFilterChange = useCallback((filters: ColumnFilter[]) => {
    setColumnFilters(filters);
  }, []);

  /**
   * Handle sort change
   */
  const handleSortChange = useCallback((sort: ColumnSort | null) => {
    setColumnSort(sort);
  }, []);

  /**
   * Handle refresh - clears cache and fetches fresh data from database
   */
  const handleRefresh = useCallback(async () => {
    // Clear notes cache to ensure fresh visit counts
    StoreService.invalidateNotesCache();
    
    // refreshStores clears cache and fetches fresh data from database
    refreshStores();
    
    // If user has a search term, refetch the search list so it reflects current Firebase data
    setSearchRefreshKey(prev => prev + 1);
    
    // Also reload total stores count
    try {
      const storesRef = collection(db, 'Stores');
      const querySnapshot = await getDocs(storesRef);
      const totalCount = querySnapshot.size;
      setTotalStoresCount(totalCount);
      console.log(`[StoreManagement] Total stores count refreshed: ${totalCount}`);
    } catch (error) {
      console.error('[StoreManagement] Error refreshing total stores count:', error);
    }
  }, [refreshStores]);

  /**
   * Core export runner — handles both "all from DB" and "filtered/selected" modes.
   * Shows a progress overlay while fetching and building the file.
   */
  const runExport = useCallback(async (
    format: 'CSV' | 'Excel',
    scope: 'all' | 'filtered' | 'selected'
  ) => {
    setShowExportDropdown(false);
    setShowBulkExportDropdown(false);
    setIsExporting(true);

    const dateStr = new Date().toISOString().split('T')[0];

    try {
      let storesToExport: Store[];

      if (scope === 'all') {
        // Fetch every non-archived store directly from Firestore
        setExportProgress({ visible: true, phase: 'fetching', loaded: 0, total: 0, format });
        storesToExport = await CSVExportService.fetchAllStoresForExport((loaded, total) => {
          setExportProgress(prev => ({ ...prev, loaded, total }));
        });
      } else if (scope === 'selected' && selectedStoreIds.length > 0) {
        storesToExport = displayedStores.filter(s => selectedStoreIds.includes(s.id));
      } else {
        storesToExport = displayedStores;
      }

      if (storesToExport.length === 0) {
        toast.error('No stores to export');
        return;
      }

      setExportProgress(prev => ({ ...prev, visible: true, phase: 'building', total: storesToExport.length }));

      const suffix = scope === 'all' ? '_all' : scope === 'selected' ? '_selected' : '_filtered';

      if (format === 'CSV') {
        await CSVExportService.exportStoresToCSV(storesToExport, {
          filename: `stores_export${suffix}_${dateStr}.csv`
        });
      } else {
        await CSVExportService.exportStoresToXLSX(storesToExport, {
          filename: `stores_export${suffix}_${dateStr}.xls`
        });
      }

      setExportProgress(prev => ({ ...prev, phase: 'done' }));
      setTimeout(() => setExportProgress(prev => ({ ...prev, visible: false })), 1200);

      toast.success(`Exported ${storesToExport.length} store${storesToExport.length !== 1 ? 's' : ''} to ${format}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export stores. Please try again.');
      setExportProgress(prev => ({ ...prev, visible: false }));
    } finally {
      setIsExporting(false);
    }
  }, [selectedStoreIds, displayedStores]);

  const handleExportCSV = useCallback((fromBulkActions: boolean = false) => {
    void runExport('CSV', fromBulkActions && selectedStoreIds.length > 0 ? 'selected' : 'filtered');
  }, [runExport, selectedStoreIds]);

  const handleExportExcel = useCallback((fromBulkActions: boolean = false) => {
    void runExport('Excel', fromBulkActions && selectedStoreIds.length > 0 ? 'selected' : 'filtered');
  }, [runExport, selectedStoreIds]);

  /**
   * Handle select all - selects all displayed stores (including filtered)
   */
  const handleSelectAll = useCallback(() => {
    if (selectedStoreIds.length === displayedStores.length) {
      // Deselect all
      setSelectedStoreIds([]);
    } else {
      // Select all displayed stores
      setSelectedStoreIds(displayedStores.map(s => s.id));
    }
  }, [displayedStores, selectedStoreIds]);

  // Close export dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
      if (bulkExportDropdownRef.current && !bulkExportDropdownRef.current.contains(event.target as Node)) {
        setShowBulkExportDropdown(false);
      }
    };
    
    if (showExportDropdown || showBulkExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportDropdown, showBulkExportDropdown]);

  /**
   * Handle store view — opens store details in a new tab
   */
  const handleViewStore = useCallback((store: Store) => {
    const url = `${window.location.origin}/manager/store-details/${store.id}?tab=store-visit`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  /**
   * Open create store form
   */
  const handleCreateStore = useCallback(() => {
    setEditingStore(undefined);
    setShowForm(true);
  }, []);

  /**
   * Open edit store form
   */
  const handleEditStore = useCallback((store: Store) => {
    setEditingStore(store);
    setShowForm(true);
  }, []);

  /**
   * Handle delete store
   */
  const handleDeleteStore = useCallback(async (store: Store) => {
    if (!user?.id) return;
    
    if (window.confirm(t('storeManagement.deleteConfirm', `Are you sure you want to delete "${store.name}"? This will mark the store as inactive.`))) {
      const reason = prompt('Please provide a reason for deletion:');
      if (reason !== null) {
        try {
          const result = await StoreService.deleteStore(store.id, user.id, false);
          if (result.success) {
            setSuccessMessage('Store deleted successfully!');
            // Remove from local state immediately so table updates without waiting for refetch
            setDeletedStoreIds(prev => new Set(prev).add(store.id));
            setAllStoresForSearch(prev => prev.filter(s => s.id !== store.id));
            setAssignedStores(prev => prev.filter(s => s.id !== store.id));
            setSearchRefreshKey(prev => prev + 1); // Force search list refetch when search is active
            handleRefresh();
          } else {
            alert(`Failed to delete store: ${result.error}`);
          }
        } catch (error) {
          console.error(t('storeManagement.deleteError', 'Error deleting store:'), error);
          alert('Failed to delete store');
        }
      }
    }
  }, [user, handleRefresh, t]);

  /**
   * Handle batch delete selected stores
   */
  const handleBatchDelete = useCallback(async () => {
    if (!user?.id) return;
    if (selectedStoreIds.length === 0) return;

    const storeCount = selectedStoreIds.length;
    const confirmMessage = `⚠️ WARNING: This will PERMANENTLY DELETE ${storeCount} store${storeCount !== 1 ? 's' : ''} from the database.\n\nThis action CANNOT be undone!\n\nAre you absolutely sure you want to continue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Double confirmation for permanent deletion
    const doubleConfirm = window.confirm(`FINAL CONFIRMATION: You are about to permanently delete ${storeCount} store${storeCount !== 1 ? 's' : ''}. Click OK to proceed.`);
    if (!doubleConfirm) {
      return;
    }

    const reason = prompt('Please provide a reason for deletion:');
    if (reason === null || reason.trim() === '') {
      toast.error('Deletion reason is required');
      return;
    }

    setIsSubmitting(true);
    
    try {
      let successCount = 0;
      let failureCount = 0;
      const errors: string[] = [];

      // Delete stores one by one (hard delete - permanently removes from database)
      const succeededIds = new Set<string>();
      for (const storeId of selectedStoreIds) {
        try {
          const result = await StoreService.deleteStore(storeId, user.id, true);
          if (result.success) {
            successCount++;
            succeededIds.add(storeId);
          } else {
            failureCount++;
            errors.push(`Store ${storeId}: ${result.error}`);
          }
        } catch (error) {
          failureCount++;
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          errors.push(`Store ${storeId}: ${errorMessage}`);
        }
      }

      // Remove successfully deleted stores from local state immediately
      if (succeededIds.size > 0) {
        setDeletedStoreIds(prev => new Set([...prev, ...succeededIds]));
        setAllStoresForSearch(prev => prev.filter(s => !succeededIds.has(s.id)));
        setAssignedStores(prev => prev.filter(s => !succeededIds.has(s.id)));
        setSearchRefreshKey(prev => prev + 1);
      }

      // Clear selection
      setSelectedStoreIds([]);

      // Show results
      if (failureCount === 0) {
        setSuccessMessage(`Successfully permanently deleted ${successCount} store${successCount !== 1 ? 's' : ''}!`);
        toast.success(`Successfully permanently deleted ${successCount} store${successCount !== 1 ? 's' : ''}`);
      } else if (successCount === 0) {
        const errorMsg = `Failed to delete all ${failureCount} store${failureCount !== 1 ? 's' : ''}`;
        console.error('Batch delete errors:', errors);
        toast.error(errorMsg);
        alert(`${errorMsg}\n\nErrors:\n${errors.join('\n')}`);
      } else {
        const mixedMsg = `Permanently deleted ${successCount} store${successCount !== 1 ? 's' : ''}, but ${failureCount} failed`;
        console.error('Batch delete errors:', errors);
        toast(mixedMsg);
        alert(`${mixedMsg}\n\nErrors:\n${errors.join('\n')}`);
      }

      // Refresh the store list
      handleRefresh();
    } catch (error) {
      console.error('Batch delete error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Batch delete failed: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [user, selectedStoreIds, handleRefresh]);

  /**
   * Handle form submission (create or update)
   */
  const handleFormSubmit = useCallback(async (storeData: Partial<Store>) => {
    if (!user?.id) {
      alert('User not authenticated. Please log in again.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      console.log('Store form submission received:', {
        storeId: editingStore?.id,
        storeData: { ...storeData, assignedToUserIds: storeData.assignedToUserIds },
        assignedUserIdsCount: storeData.assignedToUserIds?.length || 0
      });
      
      // Extract assigned user IDs if present
      const assignedUserIds = storeData.assignedToUserIds;
      const storeDataWithoutAssignments = { ...storeData };
      delete storeDataWithoutAssignments.assignedToUserIds;
      
      if (editingStore) {
        // Update existing store
        console.log('Updating store:', editingStore.id);
        const result = await StoreService.updateStore(editingStore.id, storeDataWithoutAssignments, user.id);
        
        console.log('Store update result:', result);
        
        if (result.success) {
          // Handle store assignments if provided
          if (assignedUserIds && Array.isArray(assignedUserIds)) {
            console.log('Processing store assignments:', {
              assignedUserIds,
              editingStoreAssignedIds: editingStore.assignedToUserIds || []
            });
            
            const assignmentService = new StoreAssignmentService();
            
            // Fetch current assigned user IDs from Firestore to get the actual state
            let currentAssignedIds: string[] = [];
            try {
              const storeRef = doc(db, 'Stores', editingStore.id);
              const storeSnap = await getDoc(storeRef);
              
              if (storeSnap.exists()) {
                const storeData = storeSnap.data();
                currentAssignedIds = storeData.assignedToUserIds || [];
                console.log('Fetched current assigned user IDs from Firestore:', currentAssignedIds);
              } else {
                console.warn('Store document not found in Firestore');
              }
            } catch (error) {
              console.error('Error fetching current assignments from Firestore:', error);
              // Fallback to editingStore data
              currentAssignedIds = editingStore.assignedToUserIds || [];
            }
            
            // If still no assignments, check for SalesGroup matching (these might be implicit)
            if (currentAssignedIds.length === 0) {
              const storeSalesGroup = ((editingStore as any).SalesGroup || editingStore.salesGroup || '').toUpperCase();
              if (storeSalesGroup) {
                const matchingUserIds = salesReps
                  .filter(rep => (rep.salesGroup || '').toUpperCase() === storeSalesGroup)
                  .map(rep => rep.id);
                console.log('Found matching users by SalesGroup (implicit):', matchingUserIds);
                // Don't set currentAssignedIds to these - we want to add them as new assignments
                // This way, if user checks an additional user, we'll properly save all selected users
              }
            }
            
            // Find users to add (new assignments)
            // Compare the new list to what's actually in the database
            const usersToAdd = assignedUserIds.filter(
              userId => !currentAssignedIds.includes(userId)
            );
            
            // Find users to remove (unassignments)
            // Only remove users that are in the database but not in the new selection
            const usersToRemove = currentAssignedIds.filter(
              userId => !assignedUserIds.includes(userId)
            );
            
            console.log('Assignment changes:', {
              assignedUserIds,
              currentAssignedIds,
              usersToAdd,
              usersToRemove,
              usersToAddCount: usersToAdd.length,
              usersToRemoveCount: usersToRemove.length
            });
            
            // If there are no changes, still proceed but log it
            if (usersToAdd.length === 0 && usersToRemove.length === 0) {
              console.log('No assignment changes detected - all users already assigned');
              // Continue anyway - assignments might already be correct
            }
            
            // Get user data for assignment
            const getUserData = (userId: string) => {
              const rep = salesReps.find(r => r.id === userId);
              if (!rep) {
                console.warn(`Sales rep not found for userId: ${userId}`);
              }
              return {
                username: rep?.username || '',
                name: rep?.name || 'Unknown'
              };
            };
            
            // Add new assignments
            let addedCount = 0;
            let failedToAdd: string[] = [];
            
            if (usersToAdd.length > 0) {
              console.log(`Adding ${usersToAdd.length} user(s) to store ${editingStore.id}`);
              
              for (const userId of usersToAdd) {
                try {
                  const userData = getUserData(userId);
                  if (!userData.username) {
                    console.warn(`No username found for user ${userId}, skipping assignment`);
                    failedToAdd.push(userId);
                    continue;
                  }
                  
                  console.log(`Assigning store ${editingStore.id} to user ${userId} (${userData.username})`);
                  
                  await assignmentService.assignStoreToUser(
                    editingStore.id,
                    userId,
                    userData.username,
                    user.id,
                    'manual',
                    `Assigned via store edit form`
                  );
                  
                  addedCount++;
                  console.log(`✅ Successfully assigned store to user ${userId}`);
                } catch (error: any) {
                  const errorMessage = error?.message || 'Unknown error';
                  console.error(`❌ Failed to assign store to user ${userId}:`, errorMessage, error);
                  failedToAdd.push(userId);
                  // Don't stop processing other users - continue with next user
                }
              }
            } else {
              console.log('No new users to add');
            }
            
            // Remove unassignments
            let removedCount = 0;
            let failedToRemove: string[] = [];
            
            if (usersToRemove.length > 0) {
              console.log(`Removing ${usersToRemove.length} user(s) from store ${editingStore.id}`);
              
              for (const userId of usersToRemove) {
                try {
                  console.log(`Unassigning store ${editingStore.id} from user ${userId}`);
                  
                  await assignmentService.removeStoreAssignment(
                    editingStore.id,
                    userId,
                    user.id,
                    'manual'
                  );
                  
                  removedCount++;
                  console.log(`✅ Successfully unassigned store from user ${userId}`);
                } catch (error: any) {
                  const errorMessage = error?.message || 'Unknown error';
                  console.error(`❌ Failed to unassign store from user ${userId}:`, errorMessage, error);
                  failedToRemove.push(userId);
                  // Don't stop processing other users - continue with next user
                }
              }
            } else {
              console.log('No users to remove');
            }
            
            console.log('Assignment summary:', {
              addedCount,
              removedCount,
              failedToAdd,
              failedToRemove
            });
            
            // Show success/error message
            if (failedToAdd.length > 0 || failedToRemove.length > 0) {
              const errorMsg = `Store updated, but some assignments failed: ${failedToAdd.length} failed to add, ${failedToRemove.length} failed to remove`;
              console.error('Assignment errors:', { failedToAdd, failedToRemove });
              setSuccessMessage(errorMsg);
              alert(errorMsg); // Show alert for errors
            } else if (addedCount > 0 || removedCount > 0) {
              const successMsg = `Store updated successfully! ${addedCount} user(s) assigned, ${removedCount} user(s) unassigned.`;
              console.log('Assignments completed successfully:', { addedCount, removedCount });
              setSuccessMessage(successMsg);
            } else {
              console.log('No assignment changes to process');
              setSuccessMessage('Store updated successfully!');
            }
          } else {
            // No assignments provided, just update the store
            console.log('No assignments to process');
            setSuccessMessage('Store updated successfully!');
          }
          
          setShowForm(false);
          setEditingStore(undefined);
          handleRefresh();
        } else {
          console.error('Store update failed:', result.error);
          alert(`Failed to update store: ${result.error || 'Unknown error'}`);
        }
      } else {
        // Create new store
        const result = await StoreService.createStore(storeDataWithoutAssignments as any, user.id);
        if (result.success) {
          setSuccessMessage('Store created successfully!');
          setShowForm(false);
          handleRefresh();
        } else {
          alert(`Failed to create store: ${result.error}`);
        }
      }
    } catch (error) {
      console.error('Store form submission error:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [editingStore, user, handleRefresh, salesReps]);

  /**
   * Handle form cancellation
   */
  const handleFormCancel = useCallback(() => {
    setShowForm(false);
    setEditingStore(undefined);
  }, []);

  /**
   * Handle assign sales rep
   */
  const handleAssignSalesRep = useCallback((store: Store) => {
    // This would open a modal or navigate to assignment screen
    console.log('Assign sales rep to:', store.id);
  }, []);

  return (
    <PageContainer>
      <MainContent>
        <Header>
          <Title>Store Management</Title>

          {/* Tabs for hand-lead users - show assigned vs all stores */}
          {isHandLead && (
            <TabContainer>
              <Tab 
                active={activeTab === 'assigned'} 
                onClick={() => {
                  setActiveTab('assigned');
                  setSearchTerm(''); // Clear search when switching tabs
                  setSelectedStoreIds([]); // Clear selection
                }}
              >
                <Star size={18} />
                {t('storeVisit.myStores', 'My Stores')}
                <TabBadge active={activeTab === 'assigned'}>
                  {assignedStores.length}
                </TabBadge>
              </Tab>
              <Tab 
                active={activeTab === 'all'} 
                onClick={() => {
                  setActiveTab('all');
                  setSearchTerm(''); // Clear search when switching tabs
                  setSelectedStoreIds([]); // Clear selection
                }}
              >
                <Globe size={18} />
                {t('storeVisit.allStores', 'All Stores')}
                <TabBadge active={activeTab === 'all'}>
                  {totalStoresCount || stores.length}
                </TabBadge>
              </Tab>
            </TabContainer>
          )}
        </Header>

        {/* Toolbar */}
        <Toolbar>
          <ToolbarSection>
            <SearchBar>
              <Search />
              <SearchInput
                type="text"
                placeholder="Search by name, customer #, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>
            {searchTerm && searchTerm !== debouncedSearchTerm && (
              <span style={{ fontSize: '12px', color: ThemeColors.text.hint, marginLeft: '8px' }}>
                Searching...
              </span>
            )}
          </ToolbarSection>

          <ToolbarSection>
            <Button variant="primary" onClick={handleCreateStore}>
              <Plus />
              New Store
            </Button>
            <Button variant="secondary" onClick={() => navigate('/manager/stores/import')}>
              <Upload />
              Import Stores
            </Button>
            <Button variant="secondary" onClick={handleRefresh} title="Refresh store list">
              {isLoading ? <SpinningIcon size={16} /> : <RefreshCw />}
              Refresh
            </Button>
            <ExportDropdownContainer ref={exportDropdownRef}>
              <Button 
                variant="secondary" 
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                disabled={isExporting}
              >
                {isExporting ? <SpinningIcon size={16} /> : <Download />}
                Export
                <ChevronDown size={14} style={{ marginLeft: '4px' }} />
              </Button>
              <ExportDropdown show={showExportDropdown}>
                {/* All Stores — always fetches entire Firestore database */}
                <ExportDropdownSectionLabel>
                  All Stores ({totalStoresCount || '—'})
                </ExportDropdownSectionLabel>
                <ExportDropdownItem onClick={() => runExport('CSV', 'all')}>
                  <Database size={16} />
                  Export All as CSV
                </ExportDropdownItem>
                <ExportDropdownItem onClick={() => runExport('Excel', 'all')}>
                  <Database size={16} />
                  Export All as Excel
                </ExportDropdownItem>
              </ExportDropdown>
            </ExportDropdownContainer>
          </ToolbarSection>
        </Toolbar>


        {/* Bulk Actions Bar */}
        <BulkActionsBar visible={selectedStoreIds.length > 0}>
          <SelectedCount>
            {selectedStoreIds.length} store{selectedStoreIds.length !== 1 ? 's' : ''} selected
            {selectedStoreIds.length < displayedStores.length && (
              <span style={{ marginLeft: '8px', color: ThemeColors.text.hint, fontWeight: 'normal' }}>
                (of {displayedStores.length} filtered)
              </span>
            )}
          </SelectedCount>
          <ToolbarSection>
            {selectedStoreIds.length < displayedStores.length && (
              <Button variant="secondary" onClick={handleSelectAll}>
                Select All {displayedStores.length}
              </Button>
            )}
            <ExportDropdownContainer ref={bulkExportDropdownRef}>
              <Button 
                variant="primary" 
                onClick={() => setShowBulkExportDropdown(!showBulkExportDropdown)}
                disabled={isExporting}
              >
                {isExporting ? <SpinningIcon size={16} /> : <Download />}
                Export Selected
                <ChevronDown size={14} style={{ marginLeft: '4px' }} />
              </Button>
              <ExportDropdown show={showBulkExportDropdown}>
                <ExportDropdownSectionLabel>
                  Selected ({selectedStoreIds.length})
                </ExportDropdownSectionLabel>
                <ExportDropdownItem onClick={() => runExport('CSV', 'selected')}>
                  <Download size={16} />
                  Export as CSV
                </ExportDropdownItem>
                <ExportDropdownItem onClick={() => runExport('Excel', 'selected')}>
                  <FileSpreadsheet size={16} />
                  Export as Excel
                </ExportDropdownItem>
                <ExportDropdownDivider />
                <ExportDropdownSectionLabel>
                  All Stores ({totalStoresCount || '—'})
                </ExportDropdownSectionLabel>
                <ExportDropdownItem onClick={() => runExport('CSV', 'all')}>
                  <Database size={16} />
                  Export All as CSV
                </ExportDropdownItem>
                <ExportDropdownItem onClick={() => runExport('Excel', 'all')}>
                  <Database size={16} />
                  Export All as Excel
                </ExportDropdownItem>
              </ExportDropdown>
            </ExportDropdownContainer>
            <Button 
              variant="danger" 
              onClick={handleBatchDelete}
              disabled={isSubmitting}
            >
              {isSubmitting ? <SpinningIcon size={16} /> : <X />}
              Delete Selected
            </Button>
            <Button variant="secondary" onClick={() => setSelectedStoreIds([])}>
              Clear Selection
            </Button>
          </ToolbarSection>
        </BulkActionsBar>

        {/* Stores Table */}
        {/* Success Message */}
        {successMessage && (
          <div style={{
            padding: Spacing.md,
            background: ThemeColors.success.light,
            color: ThemeColors.success.dark,
            borderRadius: '8px',
            marginBottom: Spacing.lg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>{successMessage}</span>
            <Button variant="secondary" onClick={() => setSuccessMessage(null)}>
              Dismiss
            </Button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            padding: Spacing.md,
            background: ThemeColors.error.light,
            color: ThemeColors.error.dark,
            borderRadius: '8px',
            marginBottom: Spacing.lg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>{error}</span>
            <Button variant="secondary" onClick={clearError}>
              Dismiss
            </Button>
          </div>
        )}

        {/* Advanced Notes Filters */}
        <AdvancedStoreFilters
          currentFilters={notesFilters}
          onFilterChange={handleNotesFilterChange}
          isLoading={isFilteringByNotes}
          salesReps={salesReps}
        />

        {/* Filtering by visits indicator */}
        {isFilteringByNotes && (
          <div style={{
            padding: Spacing.md,
            background: ThemeColors.info.light,
            color: ThemeColors.info.dark,
            borderRadius: '8px',
            marginBottom: Spacing.lg,
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.sm
          }}>
            <SpinningIcon size={16} />
            <span>Filtering stores by visit data...</span>
          </div>
        )}

        {/* Results count when notes filter is active */}
        {(notesFilters.competitors.length > 0 ||
          notesFilters.storeLevelRange !== null ||
          notesFilters.lowStockCategories.length > 0 ||
          notesFilters.needsManagerContact !== null ||
          notesFilters.hasNotes !== null) && !isFilteringByNotes && (
          <div style={{
            padding: Spacing.sm,
            marginBottom: Spacing.md,
            fontSize: Typography.fontSize.sm,
            color: ThemeColors.text.secondary
          }}>
            Showing {displayedStores.length} store{displayedStores.length !== 1 ? 's' : ''} matching visit filters
          </div>
        )}

        <StoreTable
          stores={displayedStores}
          isLoading={isLoading || isFilteringByNotes || isLoadingAllStores || (isHandLead && isLoadingAssignedStores)}
          onRowClick={handleViewStore}
          onView={handleViewStore}
          onEdit={handleEditStore}
          onDelete={handleDeleteStore}
          onAssignSalesRep={handleAssignSalesRep}
          selectable
          selectedStoreIds={selectedStoreIds}
          onSelectionChange={setSelectedStoreIds}
          showActions
          columnFilters={columnFilters}
          columnSort={columnSort}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
        />

        {/* Load More Button */}
        {/* Only show when:
            1. NOT searching (search loads all stores at once)
            2. NOT filtering by column filters (filters load all matching stores)
            3. hasMore is true (there are more stores to load)
        */}
        {!debouncedSearchTerm?.trim() && 
         !(columnFilters.length > 0 && columnFilters.some(f => f.value)) && 
         hasMore && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: Spacing.lg,
            marginBottom: Spacing.lg 
          }}>
            <Button
              variant="secondary"
              onClick={loadMore}
              disabled={isLoading || isFilteringByNotes}
            >
              {isLoading ? (
                <>
                  <SpinningIcon />
                  Loading...
                </>
              ) : (
                <>
                  Load More ({displayedStores.length} stores loaded)
                </>
              )}
            </Button>
          </div>
        )}

        {/* Store Form Modal */}
        {showForm && (
          <StoreForm
            store={editingStore}
            mode={editingStore ? 'edit' : 'create'}
            isLoading={isSubmitting}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            salesReps={salesReps}
          />
        )}
      </MainContent>

      {/* Export Progress Overlay */}
      {exportProgress.visible && (
        <ProgressOverlay>
          <ProgressCard>
            {exportProgress.phase === 'done' ? (
              <CheckCircle size={40} color={ThemeColors.success.main} />
            ) : (
              <SpinningIcon size={40} color={ThemeColors.primary.main} />
            )}
            <ProgressTitle>
              {exportProgress.phase === 'fetching' && 'Fetching Stores…'}
              {exportProgress.phase === 'building' && `Building ${exportProgress.format} File…`}
              {exportProgress.phase === 'done' && 'Export Complete!'}
            </ProgressTitle>
            <ProgressSubtitle>
              {exportProgress.phase === 'fetching' && (
                exportProgress.total > 0
                  ? `Loading store data from database`
                  : 'Connecting to database…'
              )}
              {exportProgress.phase === 'building' && 'Enriching records with visit data…'}
              {exportProgress.phase === 'done' && 'Your file has been downloaded.'}
            </ProgressSubtitle>
            {exportProgress.total > 0 && exportProgress.phase !== 'done' && (
              <>
                <ProgressBarTrack>
                  <ProgressBarFill
                    pct={exportProgress.phase === 'building'
                      ? 90
                      : Math.round((exportProgress.loaded / exportProgress.total) * 75)}
                  />
                </ProgressBarTrack>
                <ProgressCount>
                  {exportProgress.phase === 'fetching'
                    ? `${exportProgress.loaded} / ${exportProgress.total} stores`
                    : `${exportProgress.total} stores`}
                </ProgressCount>
              </>
            )}
            {exportProgress.phase === 'done' && (
              <ProgressBarTrack>
                <ProgressBarFill pct={100} />
              </ProgressBarTrack>
            )}
          </ProgressCard>
        </ProgressOverlay>
      )}
    </PageContainer>
  );
};

